import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { CallCategory } from "@expert/shared-types";
import type { LiteralUnion } from "type-fest";

import { getLogger } from "@expert/logging";
import { createImmerStore } from "@expert/state-utils";
import { freeze } from "immer";
import { useShallow } from "zustand/react/shallow";

import type { GuideOption, GuideSelectionObject, UiMetadata } from "../logic-builder";
import type { OpenGuideSource } from "../types";

import { defaultUiMetadata } from "./defaults";

const logger = getLogger({
    module: "useGuideStore",
});

export interface SessionMetadata {
    [key: string]: unknown;
}

export interface AnalyticsInfo {
    currentStepId: string;
    completedSteps: string;
    completedStepCount: number;
}

export interface MonitoringProps {
    dispatcher: AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;
}

export interface GuideSessionState {
    analyticsInfo: AnalyticsInfo;
    availableGuides: GuideSelectionObject[];
    currentGuideName?: string;
    disableAutomaticOpen: boolean;
    errorStateSessionId?: string;
    isErrorState: boolean;
    isGuideOpen: boolean;
    isLoadingSimplrSession: boolean;
    isWaitingForNextStep: boolean;
    openGuideSource?: OpenGuideSource;
    recommendation?: LiteralUnion<CallCategory, string>;
    selectedOptions: Record<string, GuideOption>;
    sessionMetadata: SessionMetadata;
    uiMetadata: UiMetadata;
}

const EMPTY_ANALYTICS_INFO: AnalyticsInfo = freeze({
    currentStepId: "",
    completedSteps: "",
    completedStepCount: 0,
});

const EMPTY_AVAILABLE_GUIDES: GuideSelectionObject[] = freeze([]);
const EMPTY_SELECTED_OPTIONS: Record<string, GuideOption> = freeze({});
const EMPTY_SESSION_METADATA: SessionMetadata = freeze({});

/**
 * Frozen empty session sentinel. Returned by `selectGuideSession`/`useGuideSession` when a
 * sessionId has no entry yet — same reference every call so consumers don't re-render on
 * unrelated session updates. Frozen deeply so accidental mutation throws in dev.
 */
export const defaultGuideSessionState: GuideSessionState = freeze(
    {
        analyticsInfo: EMPTY_ANALYTICS_INFO,
        availableGuides: EMPTY_AVAILABLE_GUIDES,
        currentGuideName: undefined,
        disableAutomaticOpen: false,
        errorStateSessionId: undefined,
        isErrorState: false,
        isGuideOpen: false,
        isLoadingSimplrSession: false,
        isWaitingForNextStep: false,
        openGuideSource: undefined,
        recommendation: undefined,
        selectedOptions: EMPTY_SELECTED_OPTIONS,
        sessionMetadata: EMPTY_SESSION_METADATA,
        uiMetadata: defaultUiMetadata,
    },
    true,
);

/** Mutable, per-session copy of the default state. Nested empties stay frozen until written. */
const newSessionState = (): GuideSessionState => ({
    analyticsInfo: { ...EMPTY_ANALYTICS_INFO },
    availableGuides: [],
    currentGuideName: undefined,
    disableAutomaticOpen: false,
    errorStateSessionId: undefined,
    isErrorState: false,
    isGuideOpen: false,
    isLoadingSimplrSession: false,
    isWaitingForNextStep: false,
    openGuideSource: undefined,
    recommendation: undefined,
    selectedOptions: {},
    sessionMetadata: {},
    uiMetadata: defaultUiMetadata,
});

/** Standalone selector for non-hook contexts (e.g. `getState()` calls). Returns the frozen
 *  sentinel for missing sessions so reference equality holds across calls. */
export const selectGuideSession = (state: GuideStore, sessionId: string): GuideSessionState =>
    state.sessions[sessionId] ?? defaultGuideSessionState;

/** Subscribe to a session's full guide state. Uses shallow equality so individual property
 *  changes (not the session object identity) drive re-renders. Returns the frozen sentinel
 *  when the session has no entry. */
export const useGuideSession = (sessionId: string) =>
    useGuideStore(useShallow((state: GuideStore) => selectGuideSession(state, sessionId)));

const ensureSession = (sessions: Record<string, GuideSessionState>, sessionId: string) => {
    if (!sessions[sessionId]) {
        sessions[sessionId] = newSessionState();
    }
    return sessions[sessionId];
};

export interface GuideStore {
    sessions: Record<string, GuideSessionState>;
    getGuideSession: (sessionId: string) => GuideSessionState;

    setAvailableGuides: (sessionId: string, availableGuides?: GuideSelectionObject[]) => void;
    setCurrentGuideName: (sessionId: string, props: MonitoringProps & { currentGuideName?: string }) => void;
    setAnalyticsInfo: (sessionId: string, analyticsInfo: AnalyticsInfo) => void;
    setDisableAutomaticOpen: (sessionId: string, value: boolean) => void;
    setErrorStateSessionId: (sessionId: string) => void;
    setIsErrorState: (sessionId: string, value: boolean) => void;
    setIsGuideOpen: (sessionId: string, value: boolean) => void;
    setIsLoadingSimplrSession: (sessionId: string, isLoading: boolean) => void;
    setIsWaitingForNextStep: (sessionId: string, isWaiting: boolean) => void;
    setOpenGuideSource: (sessionId: string, openGuideSource?: OpenGuideSource) => void;
    setSessionMetadata: (sessionId: string, sessionMetadata: SessionMetadata) => void;
    setUiMetadata: (sessionId: string, uiMetadata?: UiMetadata) => void;
    setSelectedOptions: (sessionId: string, selectedOptions: Record<string, GuideOption>) => void;
    clearSelectedOptions: (sessionId: string) => void;
    setRecommendation: (sessionId: string, recommendation?: LiteralUnion<CallCategory, string>) => void;
    clearRecommendation: (sessionId: string) => void;
    resetGuide: (sessionId: string) => void;
    deleteSession: (sessionId: string) => void;
}

/**
 * Per-session guided-flow state for both voice and chat sessions.
 *
 * Persisted to localStorage with manual rehydration (see `useGuideStore.persist.rehydrate`).
 * Empty session reads return the frozen `defaultGuideSessionState` sentinel — never inline
 * `?? {}` here, or every consumer re-renders on unrelated session writes.
 *
 * For action calls, prefer `useGuideStore.getState().setX(...)` over a selector hook —
 * actions are stable references and don't need a subscription. Use the `useGuideSession`
 * hook for reading per-session state.
 */
export const useGuideStore = createImmerStore<GuideStore>({
    persist: { name: "guide-store", skipHydration: true },
    devtools: {
        store: "simplr-guide-store",
        name: "simplr-guide-store",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    sessions: {},
    getGuideSession: (sessionId: string) => {
        return get().sessions[sessionId] ?? defaultGuideSessionState;
    },
    setAvailableGuides: (sessionId: string, availableGuides: GuideSelectionObject[] = []) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).availableGuides = availableGuides;
            },
            false,
            "setAvailableGuides",
        );
    },
    setCurrentGuideName: (
        sessionId: string,
        { dispatcher, currentGuideName }: MonitoringProps & { currentGuideName?: string },
    ) => {
        set(
            (state) => {
                const session = ensureSession(state.sessions, sessionId);
                if (session.currentGuideName && currentGuideName) {
                    // there is already a guide selected (a guide in progress)
                    logger.info(
                        { availableGuides: session.availableGuides, guide: currentGuideName },
                        "Solve Guide | open new guided flow within in progress guided flow",
                    );
                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                        guide: currentGuideName,
                        source: "manual",
                        sourceDetails: "in-progress-guide",
                    });
                } else if (currentGuideName) {
                    // a guided flow is being selected and started
                    logger.info(
                        { availableGuides: session.availableGuides, guide: currentGuideName },
                        "Solve Guide | open new guided flow",
                    );
                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                        guide: currentGuideName,
                        ...session.openGuideSource,
                    });
                }

                logger.info({ currentGuideName }, `Solve Guide | Setting currentGuideName`);
                session.currentGuideName = currentGuideName;
            },
            false,
            "setCurrentGuideName",
        );
    },
    setAnalyticsInfo: (sessionId: string, analyticsInfo: AnalyticsInfo) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).analyticsInfo = analyticsInfo;
            },
            false,
            "setAnalyticsInfo",
        );
    },
    setDisableAutomaticOpen: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).disableAutomaticOpen = value;
            },
            false,
            "setDisableAutomaticOpen",
        );
    },
    setErrorStateSessionId: (sessionId: string) => {
        set(
            (state) => {
                const session = ensureSession(state.sessions, sessionId);
                // Each call appends `_RESTARTED` so consecutive errors on the same workspace
                // session produce unique simplr session IDs (e.g. `${id}_RESTARTED_RESTARTED`).
                const base = session.errorStateSessionId ?? sessionId;
                session.errorStateSessionId = `${base}_RESTARTED`;
            },
            false,
            "setErrorStateSessionId",
        );
    },
    setIsErrorState: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isErrorState = value;
            },
            false,
            "setIsErrorState",
        );
    },
    setIsGuideOpen: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isGuideOpen = value;
            },
            false,
            "setIsGuideOpen",
        );
    },
    setIsLoadingSimplrSession: (sessionId: string, isLoading: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isLoadingSimplrSession = isLoading;
            },
            false,
            "setIsLoadingSimplrSession",
        );
    },
    setIsWaitingForNextStep: (sessionId: string, isWaiting: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isWaitingForNextStep = isWaiting;
            },
            false,
            "setIsWaitingForNextStep",
        );
    },
    setOpenGuideSource: (sessionId: string, openGuideSource?: OpenGuideSource) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).openGuideSource = openGuideSource;
            },
            false,
            "setOpenGuideSource",
        );
    },
    setSessionMetadata: (sessionId: string, sessionMetadata: SessionMetadata) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).sessionMetadata = sessionMetadata;
            },
            false,
            "setSessionMetadata",
        );
    },
    setUiMetadata: (sessionId: string, uiMetadata?: UiMetadata) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).uiMetadata = uiMetadata || defaultUiMetadata;
            },
            false,
            "setUiMetadata",
        );
    },
    setSelectedOptions: (sessionId: string, selectedOptions: Record<string, GuideOption>) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).selectedOptions = selectedOptions;
            },
            false,
            "setSelectedOptions",
        );
    },
    clearSelectedOptions: (sessionId: string) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).selectedOptions = EMPTY_SELECTED_OPTIONS;
            },
            false,
            "clearSelectedOptions",
        );
    },
    setRecommendation: (sessionId: string, recommendation?: LiteralUnion<CallCategory, string>) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).recommendation = recommendation;
            },
            false,
            "setRecommendation",
        );
    },
    clearRecommendation: (sessionId: string) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).recommendation = undefined;
            },
            false,
            "clearRecommendation",
        );
    },
    resetGuide: (sessionId: string) => {
        set(
            (state) => {
                logger.info({ sessionId }, "Solve Guide | Resetting guide state");
                const session = ensureSession(state.sessions, sessionId);
                session.analyticsInfo = { ...EMPTY_ANALYTICS_INFO };
                // Clear so a new session can't auto-open against a prior session's
                // guides or recommendation before its Simplr stream has loaded.
                session.availableGuides = [];
                session.recommendation = undefined;
                session.currentGuideName = undefined;
                session.disableAutomaticOpen = false;
                session.isErrorState = false;
                session.isLoadingSimplrSession = true;
                session.isWaitingForNextStep = false;
                session.errorStateSessionId = undefined;
                session.openGuideSource = undefined;
                session.selectedOptions = {};
                session.sessionMetadata = {};
                session.uiMetadata = defaultUiMetadata;
            },
            false,
            "resetGuide",
        );
    },
    deleteSession: (sessionId: string) => {
        set(
            (state) => {
                logger.info({ sessionId }, "Solve Guide | Deleting guide session");
                delete state.sessions[sessionId];
            },
            false,
            "deleteSession",
        );
    },
}));
